import React from 'react'
import styled from 'styled-components'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { IndexLayoutOld, setPreviewPasswordSync } from '@sketch/modules-common'
import { Button, PasswordInput, Text } from '@sketch/components'
import { StyledErrorMessage } from '../../../../views/RequiresMembershipView/RequiresMembershipView.styles'
import { ReactComponent as LockIcon } from '@sketch/icons/lock-64'
import { useGetPreviewDetailsLazyQuery } from '@sketch/gql-types'

const FormContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;

  Form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`

const ErrorText = styled(Text)`
  color: ${({ theme }) => theme.colors.inspect.selection};
  font-size: ${({ theme }) => theme.fontSizes.C};
  text-align: left;
  margin-top: -12px;
`

const validationSchema = Yup.object({
  password: Yup.string().required('Password is required'),
})

export const PasswordProtectedScreen: React.FC<{
  previewID: string
}> = ({ previewID }) => {
  const [currentPassword, setCurrentPassword] = React.useState('')

  const [getPreviewWithPasswordQuery, { loading, error }] =
    useGetPreviewDetailsLazyQuery({
      onCompleted: data => {
        if (data?.preview) {
          // sync the password after the query goes through
          setPreviewPasswordSync(previewID, currentPassword)
        }
      },
    })

  const handleSubmit = async (values: { password: string }) => {
    setCurrentPassword(values.password)

    getPreviewWithPasswordQuery({
      variables: {
        previewIdentifier: previewID,
        password: values.password,
      },
    })
  }

  return (
    <IndexLayoutOld headerLink="none">
      <StyledErrorMessage
        icon={<LockIcon />}
        iconSize="medium"
        title="Password Required"
        extra={
          <Formik
            initialValues={{ password: '' }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              isSubmitting,
              setFieldError,
            }) => {
              if (error && !('password' in errors)) {
                const hasUnauthorizedError = error?.graphQLErrors?.some(
                  (error: any) =>
                    error.extensions?.reason === 'INVALID_PASSWORD'
                )

                if (hasUnauthorizedError) {
                  setFieldError(
                    'password',
                    'Incorrect password. Please try again.'
                  )
                } else {
                  setFieldError(
                    'password',
                    'An error occurred. Please try again.'
                  )
                }
              }

              return (
                <FormContainer>
                  <Form>
                    <PasswordInput
                      name="password"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Password"
                    />
                    {touched.password && errors.password && (
                      <ErrorText>{errors.password}</ErrorText>
                    )}
                    <Button
                      fill
                      type="submit"
                      disabled={!values.password || isSubmitting || loading}
                      variant="primary"
                      loading={isSubmitting || loading}
                    >
                      Continue
                    </Button>
                  </Form>
                </FormContainer>
              )
            }}
          </Formik>
        }
        description={
          <>
            This preview requires a password to view it. Please enter the
            password that was shared with you.
          </>
        }
      />
    </IndexLayoutOld>
  )
}
